import { h, defineComponent, ref, watch, VNodeChildren } from 'vue';
import { d, n, t } from '@/ui/i18n/i18n';
import * as qs from '@/ui/queryState';
import * as q from '@/ui/routes/query';
import {
  box,
  level,
  loader,
  namedLink,
  numericColumn,
  pageTitle,
  pagination,
  sortableTextColumn,
  textColumn,
} from '@/ui/components';
import { questionList, scoresListExportUrl, SupportedExportFormat } from '@/services/api/resources/analytics';
import { AnalyticsEntityGroup, emptyDetailList, Question, QuestionList } from '@/entities/analytics';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { AnalyticsTable } from '@/ui/components/table/AnalyticsTable';
import { SortDirection } from '@/ui/types';
import { QuestionGroupBy } from '@/services/api/resources/analytics/types';
import { download } from '@/ui/helpers/uiHelper';
import { exportDropdown } from '@/ui/components/dropdown/ExportDropdown';
import { renderForbiddenPage } from '../../error/ForbiddenPage';
import { useQueryFiltering } from './useQueryFilters';
import { inspectionDetailPageLink } from '@/ui/helpers/inspectionHelper';
import { renderAnalyticsTeaserPage } from '../../teasers/AnalyticsTeaserPage';
import { BackButton } from '@/ui/components/button/BackButton';

export const ScoresDetailPage = defineComponent({
  name: 'ScoresDetailPage',
  setup() {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('conductedAt', SortDirection.Descending);
    const {
      start,
      end,
      group,
      entityItem,
      inspector,
      category,
      checklist,
      questionTitle,
      response,
      inspection,
      section,
      question,
    } = useQueryFiltering();

    // get list task
    const query = () => ({
      timezone: user.value.timezone,
      page: page.value,
      perPage: perPage.value,
      sort: sort.value,
      start: start.value,
      dir: dir.value,
      end: end.value,
      entityItem: entityItem.value,
      group: group.value,
      inspector: inspector.value,
      category: category.value,
      checklist: checklist.value,
      questionTitle: questionTitle.value,
      response: response.value,
      inspection: inspection.value,
      section: section.value,
      question: question.value,
      hasScore: true,
      excludeNegativeResponseCount: false,
    });
    const list = ref<QuestionList>(emptyDetailList);
    const getList = useTask(
      () => questionList(query()),
      userList => {
        list.value = userList;
        initialLoading.value = false;
      },
    );
    watch([
      page,
      perPage,
      group,
      entityItem,
      inspector,
      category,
      checklist,
      questionTitle,
      response,
      inspection,
      section,
      sort,
      dir,
    ], getList.run);

    // export task
    const { run: exportData } = useTask(
      (format: SupportedExportFormat | undefined) => scoresListExportUrl(
        query(),
        format as SupportedExportFormat,
        user.value.locale,
      ),
      download,
    );

    return function render() {
      return h('div', [
        h('div', [
          h('div', { class: 'columns' }, [
            h('div', { class: 'column' }, [
              level(
                [h(BackButton), pageTitle(t('analytics.inspection-items'))],
                [exportDropdown(exportData)],
              ),
              box([
                loader('div', getList,
                  [
                    h(AnalyticsTable, {
                      props: {
                        list: list.value.data,
                        sort: sort.value,
                        dir: dir.value,
                      },
                      scopedSlots: {
                        default: () => [
                          sortableTextColumn({
                            field: 'conductedAt',
                            label: t('checklist.conducted-at'),
                          }, {
                            default: ({ row }: { row: Question }) =>
                              row.data.conductedAt ? d(row.data.conductedAt) : '',
                          }),
                          textColumn({
                            field: 'title',
                            label: t('inspections.question'),
                          }, {
                            default: ({ row }: { row: Question }) => row.inspectionId && row.authorizedTo.view
                              ? [inspectionDetailPageLink(row.data.title, row.inspectionId)]
                              : row.data.title,
                          }),
                          textColumn({
                            field: 'response',
                            label: t('common.response'),
                          }, {
                            default: ({ row }: { row: Question }) => row.data.response,
                          }),
                          textColumn({
                            field: 'checklistTitle',
                            label: t('checklist.title'),
                          }, {
                            default: ({ row }: { row: Question }) => row.data.checklistTitle,
                          }),
                          textColumn({
                            field: 'conductedBy',
                            label: t('checklist.conducted-by'),
                          }, {
                            default: ({ row }: { row: Question }) =>
                              row.data.conductedBy ? row.data.conductedBy.data.name : '',
                          }),
                          numericColumn({
                            field: 'score',
                            label: t('common.score'),
                          }, {
                            default: ({ row }: { row: Question }) => n(row.data.score, 'analyticsInteger'),
                          }),
                        ],
                      },
                      on: {
                        sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                          resetPagination();
                          sort.value = field;
                          dir.value = direction;
                        }),
                      },
                    }),
                    (list.value.meta.total)
                      ? pagination({
                        current: page.value,
                        total: list.value.meta.total,
                        perPage: perPage.value,
                      })
                      : null,
                  ], !initialLoading.value),
              ]),
            ]),
          ]),
        ]),
      ]);
    };
  },
});

interface Query {
  start: Date;
  end: Date;
  entityItem: number[];
  group: number[];
  inspector: number[];
  category: number[];
  checklist: number[];
  question: string[];
  questionTitle: string[];
  response: string[];
  inspection: number[];
  section: string[];
}

export const scoresDetailPageLink = (
  anchor: VNodeChildren,
  groupBy: QuestionGroupBy,
  group: AnalyticsEntityGroup,
  query: Query,
) => namedLink(
  anchor,
  'analyticsScoresExplain',
  undefined,
  {
    start: q.dateEncoder(group.start || query.start),
    end: q.dateEncoder(group.end || query.end),
    entityItem: groupBy === QuestionGroupBy.EntityItem
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.entityItem),
    group: groupBy === QuestionGroupBy.Group
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.group),
    inspector: groupBy === QuestionGroupBy.Inspector
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.inspector),
    category: groupBy === QuestionGroupBy.Category
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.category),
    checklist: groupBy === QuestionGroupBy.Checklist
      ? q.nullableStringEncoder(group.id)
      : q.numbersEncoder(query.checklist),
    question: groupBy === QuestionGroupBy.Question
      ? q.nullableStringEncoder(group.id)
      : q.stringsEncoder(query.question),
    questionTitle: q.stringsEncoder(query.questionTitle),
    response: q.stringsEncoder(query.response),
    inspection: q.numbersEncoder(query.inspection),
    section: groupBy === QuestionGroupBy.Section
      ? q.nullableStringEncoder(group.id)
      : q.stringsEncoder(query.section),
  },
);
